import { useQuery } from '@tanstack/react-query';
import { AddressDto, AddressListResult, AddressListResultDao, SiteAddressViewDto } from 'src/models/address';
import { SiteDto } from 'src/models/relationshipmanagement';
import { getAddressLookupFind, getAddressLookupRetrieve, getSiteAddresses } from 'src/services/components/vigo-address';
import { getCustomerSites, getSiteStatuses, getSiteTypes } from 'src/services/relationshipmanagement';

interface AddressListResultCustom extends AddressListResult {
  isMainSite?: boolean;
}

export const useSearchResultsQuery = (
  searchTerm: string,
  companyID: string,
  countryIso3: string,
  onSuccess: (data: AddressListResultDao) => any,
  pEnabled: boolean
) => {
  return useQuery({
    queryKey: ['addressSearchResults', { searchTerm, companyID, countryIso3 }],
    queryFn: async () => {
      const response = await getAddressLookupFind(encodeURI(searchTerm), companyID, countryIso3);

      return response.data;
    },
    onSuccess: data => {
      if (typeof onSuccess === 'function') {
        onSuccess(data);
      }
    },
    enabled: pEnabled,
    refetchOnWindowFocus: false
  });
};

export const useSiteAddressQuery = (companyID: string, pEnabled: boolean) => {
  return useQuery({
    queryKey: [companyID],
    queryFn: async () => {
      const response = await getSiteAddresses(companyID, true);

      const converted = response.data.map((item: SiteAddressViewDto) => {
        const lines: (string | undefined)[] = [item.siteName, item.line1, item.city, item.postalCode];

        const convertedItem: AddressListResult = {
          siteAddressID: item.id,
          text: lines.filter(Boolean).join(', ')
        };

        return convertedItem;
      });

      return converted;
    },
    enabled: pEnabled,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
    cacheTime: 2 * 60 * 1000
  });
};

export const useSiteAddressQueryV2 = (companyID: string, pEnabled: boolean) => {
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['siteAddressesV2', companyID],
    queryFn: async () => {
      const mainSiteType = getSiteTypes({ filter: `isMain eq true` });
      const siteStatus = getSiteStatuses({ filter: `isSiteActive eq true` });

      const res = await Promise.all([mainSiteType, siteStatus]);

      const sites = await getCustomerSites(undefined, undefined, {
        filter: `companyId eq guid'${companyID}'`
      });

      // filter sites by active site statuses
      const filteredSites = sites.data.filter(site => res[1].data.some(status => status.id === site.siteStatusId));

      const converted = filteredSites.map((item: SiteDto) => {
        const lines: (string | undefined)[] = [
          item.name,
          item.address?.line1,
          item.address?.city,
          item.address?.postalCode
        ];

        const convertedItem: AddressListResultCustom = {
          siteAddressID: item.id,
          isMainSite: item.siteTypeId === res[0].data[0].id,
          text: lines.filter(Boolean).join(', ')
        };

        return convertedItem;
      });

      return converted;
    },
    enabled: pEnabled,
    refetchOnWindowFocus: false
  });
};

export const useSelectedAddressQuery = (
  selectedId: string,
  countryCode: string,
  onSuccess: (data: AddressDto) => any,
  pEnabled: boolean
) => {
  return useQuery({
    queryKey: ['selectedAddress', { selectedId }, countryCode],
    onSuccess: data => {
      if (typeof onSuccess === 'function') {
        onSuccess(data);
      }
    },
    queryFn: async () => {
      const response = await getAddressLookupRetrieve(selectedId, countryCode);

      const result: AddressDto = {};
      const notRequiredProperties = [
        'lookUpId',
        'search',
        // 'id',
        'ownerTenantId',
        'ownerSiteId',
        'createdOn',
        'createdBy',
        'updatedOn',
        'updatedBy',
        'isDeleted',
        'version',
        'attachmentCount'
      ];

      Object.entries(response.data).forEach(([key, value]) => {
        if (notRequiredProperties.includes(key)) {
          return;
        }

        result[key as keyof AddressDto] = value;
      });

      return result;
    },
    enabled: pEnabled,
    refetchOnWindowFocus: false
  });
};
